import React, { useState } from 'react';
import { duckify, currentCustomer, currentUser, singleDuck, dependentDuck, filterDuck } from 'utils/duckify';
import { billingAccounts, billingAccountAccesses, billingAccountPotentialPayers } from '../../../ducks';
import ManagedResourceList from '../../shared/ManagedResourceList';
import FontAwesome from '../../../utils/FA';
import { faTimes } from '@fortawesome/pro-solid-svg-icons/faTimes';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { faUsers } from '@fortawesome/pro-solid-svg-icons/faUsers';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons/faSpinner';
import { compose } from 'redux';
import withFiltering from '../../../hocs/withFiltering';
import { withRouter } from 'react-router';
import Placeholder from '../../Placeholder';

import './styles/BillingAccountAccessList.scss';

const AddBillingAccountAccess =
  duckify(
    // mapDucksToProps
    ({billingAccount, user_id}) => ({
      currentCustomer,
      contacts: filterDuck(billingAccountPotentialPayers, {billing_account_id: billingAccount.id})
    })
  )(
    // render
    ({billingAccount, contacts, currentCustomer, allDucksReady, createBillingAccountAccess}) => {
      const [adding, setAdding] = useState(false);
      const [selectedContactId, setSelectedContactId] = useState();
      const [submitting, setSubmitting] = useState(false);

      if(!adding) {
        return <button type={'button'}
                className={'btn btn-primary'}
                onClick={() => setAdding(true)}
                >
          Add Additional Contact
        </button>;
      }

      const viableContacts = contacts.filter((contact) => !billingAccount.billing_account_accesses.find(baa => baa.contact_id === contact.id));

      const selectedContact = viableContacts.find(c => c.id === selectedContactId);

      return <Placeholder.loading ready={allDucksReady} content={() =>
        <React.Fragment>
          {!!selectedContact && !selectedContact.email &&
            <div className={'alert alert-danger'}>
              We don’t have an email on file for this contact. Please contact {currentCustomer.name} to add one before they can be granted shared billing access.
            </div>
          }
          <div>
            <div className={'add-account-access-selector'}>
              <select className={'form-select'}
                      onChange={e => setSelectedContactId(e.target.value)}
                      value={selectedContactId}
              >
                <option value={''}>Select Contact</option>
                { viableContacts.map(c => <option value={c.id}>{c.name}</option>) }
              </select>
            </div>

            <div className={'add-account-access-buttons'}>
              <div className={'input-group-btn'}>
                <button
                  type={'button'}
                  className={'btn btn-light'}
                  onClick={() => {
                    setAdding(false);
                  }}
                >
                  <FontAwesome icon={faTimes} />
                </button>

                <button
                  type={'button'}
                  className={'btn btn-primary'}
                  disabled={submitting || !selectedContact || !selectedContact.email}
                  onClick={
                    () => {
                      setSubmitting(true);

                      createBillingAccountAccess({billing_account_id: billingAccount.id, contact_id: selectedContactId})
                      .then(() => {
                        setAdding(false);
                      })
                      .finally(() => {
                        setSubmitting(false);
                      })
                    }
                  }
                >
                  {
                    submitting && <FontAwesome icon={faSpinner} spin/>
                  }
                  {
                    !submitting && <FontAwesome icon={faCheck}/>
                  }
                </button>
              </div>
            </div>
          </div>
        </React.Fragment>
      }/>;
    }
  );


const BillingAccountAccessList = ({
  billingAccounts,
  filters,
  currentUser,
  currentCustomer,
  allDucksReady,
  deleteBillingAccountAccess,
  createBillingAccountAccess
}) => {
  // Pop in instead of placeholder.  Too many placeholders looks busy on this page
  if (!allDucksReady || !billingAccounts.some(x=>x)) {
    return <React.Fragment></React.Fragment>;
  }

  const billingAccount = (filters && filters.billing_account_id) ?
    billingAccounts.find(account => account.id === filters.billing_account_id) :
    billingAccounts[0];

  // Hide if no billing account, or if current user is not primary owner
  // Pop in instead of placeholder.  Too many placeholders looks busy on this page
  if (!billingAccount || billingAccount.user_id !== currentUser.id) {
    return <React.Fragment></React.Fragment>;
  }

  const nonPrimaryAccesses = billingAccount.billing_account_accesses.filter(baa => baa.contact_id !== billingAccount.contact.id);

  return <div>
    <h2>
      <FontAwesome icon={faUsers} nbsp />
      Manage Shared Billing Access
    </h2>
    <p>
      If you would like to allow someone else to access Billing Management for this account,
      you can add them below. Please note autopay is not available for those with shared access.
      If you do not see the person you would like to share access with, please contact {currentCustomer.name}.
    </p>

    <ManagedResourceList
      fetchItems={() => Promise.resolve}
      items={nonPrimaryAccesses}
      itemButtons={[
        ({item, deleteItem, displayType}) =>
          <a className="managed-list-item-action btn btn-xs btn-danger"
             onClick={() => deleteItem()} data-bs-toggle="tooltip" title={`Remove Access`}>
            <FontAwesome icon={faTimes}/>
          </a>
      ]}
      displayType={'contact'}
      columns={[
        {header: 'Contact Name', value: item => item.contact.name}
      ]}
      collectionButtons={[]}
      emptyListMessage={' '}

      deleteItem={item => deleteBillingAccountAccess(item)}
      deleteConfirmBody={item => (
        <p>
          Are you sure you would like to remove shared access for this account?
        </p>
      )}
    />

    <AddBillingAccountAccess
      billingAccount={billingAccount}
      createBillingAccountAccess={createBillingAccountAccess}
      user_id={currentUser.id}
    />
  </div>
};

const mapDucksToProps = ({ filters }) => ({
  currentCustomer,
  currentUser,
  billingAccounts: dependentDuck(currentUser, ({value: currentUser}) =>
    filterDuck(billingAccounts, {user_id: currentUser.id})
  ),
});

const mapDispatchToProps = (dispatch) => ({
  deleteBillingAccountAccess: (baa) => dispatch(billingAccountAccesses.actions.remove(baa)).then(
    () => dispatch(billingAccounts.actions.get(baa.billing_account_id))
  ),
  createBillingAccountAccess: (baa) => dispatch(billingAccountAccesses.actions.create({...baa, email_notifications_enabled: false})).then(
    () => dispatch(billingAccounts.actions.get(baa.billing_account_id))
  )
});

export default compose(
  withRouter,
  withFiltering,
  duckify(mapDucksToProps, ()=>({}), mapDispatchToProps)
)(BillingAccountAccessList);
